import React from 'react';
import { Switch, FormControlLabel } from '@mui/material';

const ButtonComponent = ({ 
  children,
  onClick,
  className = '',
  variant = 'secondary',
  width = 'auto',
  height = '36px',
  maxWidth = 'none',
  gap = '8px',
  borderRadius = '8px',
  padding = '8px 12px',
  fontSize = '14px',
  fontWeight = 400,
  lineHeight = '140%',
  textAlign = 'center',
  whiteSpace = 'nowrap',
  disabled = false,
  type = 'button',
  style = {},
  noBorder = false,
  color,
  leftIcon = null,
  rightIcon = null,
  leftIconSize = 'w-4 h-4',
  rightIconSize = 'w-4 h-4',
  onMouseEnter,
  onMouseLeave,
  isSwitch = false,
  switchProps = {},
  switchLabel = '',
  ...props
}) => {
  // Define different button variants
  const variants = {
    primary: {
      background: '#6366F1',
      color: 'white',
      border: '1px solid #6366F1',
      hover: 'hover:bg-purple-700'
    },
    secondary: {
      background: '#FFFFFF',
      color: 'black',
      border: '1px solid #D7D7D7',
      hover: 'hover:text-gray-700'
    },
    danger: {
      background: '#EF4444',
      color: 'white',
      border: '1px solid #EF4444',
      hover: 'hover:bg-red-700'
    },
    ghost: {
      background: 'transparent',
      color: 'black',
      border: 'none',
      hover: 'hover:text-gray-700'
    }
  };

  const currentVariant = variants[variant] || variants.secondary;

  const buttonStyle = {
    width,
    height,
    maxWidth,
    gap,
    borderRadius,
    borderWidth: (currentVariant.border !== 'none' && !noBorder) ? '1px' : '0',
    paddingTop: typeof padding === 'string' ? padding.split(' ')[0] : '8px',
    paddingRight: typeof padding === 'string' ? (padding.split(' ')[1] || padding.split(' ')[0]) : '12px',
    paddingBottom: typeof padding === 'string' ? (padding.split(' ')[2] || padding.split(' ')[0]) : '8px',
    paddingLeft: typeof padding === 'string' ? (padding.split(' ')[3] || padding.split(' ')[1] || padding.split(' ')[0]) : '12px',
    border: noBorder ? 'none' : currentVariant.border,
    background: currentVariant.background,
    boxShadow: (variant === 'secondary' && !noBorder) ? '0px 1px 2px 0px #0000000A' : 'none',
    fontFamily: 'Inter',
    fontWeight,
    fontStyle: 'normal',
    fontSize,
    lineHeight,
    textAlign,
    color: color || currentVariant.color,
    whiteSpace,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...style
  };

  if (isSwitch) {
    // Custom switch using provided class string
    const {
      checked = false,
      onChange = () => {},
      disabled: switchDisabled = false,
      inputProps = {},
    } = switchProps;
    // metaEnhancements can be passed via switchProps for opacity
    const metaEnhancements = switchProps?.metaEnhancements !== undefined ? switchProps.metaEnhancements : true;
    return (
      <label className={`relative inline-flex items-center cursor-pointer ${className}`} style={style}>
        <input
          type="checkbox"
          className="sr-only peer"
          checked={checked}
          onChange={onChange}
          disabled={switchDisabled}
          {...inputProps}
        />
        <div
          className={`w-8 h-4 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#6E65ED] rounded-full peer peer-checked:after:translate-x-4 peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[1px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-[#6E65ED] ${!metaEnhancements ? 'opacity-50' : ''}`}
        ></div>
        <span className="ml-2 text-sm font-medium text-[#1F1F1F]">{switchLabel || children}</span>
      </label>
    );
  }
  return (
    <button
      className={`${currentVariant.hover} ${className}`}
      style={buttonStyle}
      onClick={onClick}
      disabled={disabled}
      type={type}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      {...props}
    >
      {leftIcon && (
        <div className="flex items-center justify-center">
          {typeof leftIcon === 'string' ? (
            <img src={leftIcon} alt="Left Icon" className={leftIconSize} />
          ) : (
            leftIcon
          )}
        </div>
      )}
      {children}
      {rightIcon && (
        <div className="flex items-center justify-center">
          {typeof rightIcon === 'string' ? (
            <img src={rightIcon} alt="Right Icon" className={rightIconSize} />
          ) : (
            rightIcon
          )}
        </div>
      )}
    </button>
  );
};

export default ButtonComponent;
